import {useQuery} from '@tanstack/react-query';

import {fetchRecords} from '../services/ReportTypesService';

// FNP-918: the data layer for the report types list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was the bare string `['reportTypes']`, which collides across modules.
export const reportTypesKeys = {
    all: ['report-types'] as const,
    lists: () => [...reportTypesKeys.all, 'list'] as const,
    list: () => [...reportTypesKeys.lists()] as const,
    details: () => [...reportTypesKeys.all, 'detail'] as const,
    detail: (id: string) => [...reportTypesKeys.details(), id] as const,
};

/**
 * ⚠️ `useQuery`, NOT `useInfiniteQuery` — and this is the one module in this
 * wave where that is CORRECT rather than a gap (checklist §9).
 *
 * `listReportTypes` declares NO arguments at all:
 *
 *     query listReportTypes { listReportTypes { items { ... } } }
 *
 * No `$limit`, no `$nextToken`, and the response has no `nextToken` field to
 * return. There is nothing to paginate: report types are Stripe's fixed
 * CATALOGUE of available report formats — on the order of 30 rows, defined by
 * Stripe and changing only when Stripe ships a new report. It is a reference
 * list, not a tenant-growing dataset.
 *
 * So the §9 boxes (useInfiniteQuery, getNextPageParam, DataTableTokenPagination,
 * loadedCount) are genuinely N/A here, not skipped. Wrapping a single
 * unpaginated response in an infinite query would add a "load more" control
 * that can never load anything — an affordance with nothing behind it, which
 * is the same defect class the checklist warns about for selection checkboxes.
 *
 * If Stripe ever paginates this endpoint the migration is the same one the
 * other modules just had; until then, forcing it would be building for a
 * hypothetical.
 *
 * `staleTime` is set deliberately (§8): the catalogue is effectively static
 * within a session, so refetching it on every mount is pure waste. Five
 * minutes is short enough that a Stripe-side addition appears without a hard
 * reload, and the Reload button invalidates it on demand.
 */
export const useReportTypesQuery = () => {
    return useQuery({
        queryKey: reportTypesKeys.list(),
        queryFn: () => fetchRecords(),
        staleTime: 5 * 60 * 1000,
    });
};

// ⚠️ There are NO mutations here, and none are missing. The API exposes
// listReportTypes / getReportType only — the catalogue is Stripe's, so there
// is nothing for this app to create, update or delete. See the notes in
// RecordsTable and IndexPageHeader.
